import { observer } from 'mobx-react-lite'
import { useRepository } from 'hooks/auth'
import { InfoIcon } from 'components/icons/Info'
import { useTranslationTyped } from 'i18n/i18n'
import type { ProfileCubit } from '@breakoutlearning/firebase-repository/cubits/ProfileCubit'
import { useEffect } from 'react'
import type {
  BackendConfigModel,
  ConfigEntry,
} from '@breakoutlearning/firebase-repository/cubits/BackendConfigModel'
import { useBreakoutUser } from 'hooks/profile'
import { SuperAdminPrivilege } from '@breakoutlearning/firebase-repository/types'

const ProfileViewConfig = observer(function ProfileViewModerationConfig({
  title,
  config,
}: {
  title: string
  config: BackendConfigModel
}) {
  const { tt } = useTranslationTyped()
  useEffect(() => {
    config.fetchIfNotFetched()
  }, [config])

  return (
    <div className="mt-3 flex h-full flex-col">
      <h2 className="text-headline-large">{title}</h2>
      <div className="mb-1 text-body-large text-core-on-tertiary">
        {tt.roles.internal_use_only()}
      </div>
      <div className="mt-2 flex-1 rounded-3xl bg-surface p-6">
        {config.isLoaded && <ConfigViewer entries={config.entries} />}
        {config.isError && (
          <div className="text-body-medium text-core-on-tertiary">
            {tt.profile.error_loading_config({ error: config.errorMessage })}
          </div>
        )}
      </div>
    </div>
  )
})

export const ProfileViewFeatureFlags = observer(
  function ProfileViewFeatureFlags({ cubit }: { cubit: ProfileCubit }) {
    const { tt } = useTranslationTyped()
    const repository = useRepository()
    const featureFlags = repository.featureFlags.flags
    const breakoutUser = useBreakoutUser()

    const canViewThisPage =
      breakoutUser.isAdmin &&
      breakoutUser.superAdmin.isLoaded &&
      breakoutUser.isSuperAdmin &&
      breakoutUser.superAdmin.hasPrivilege(
        SuperAdminPrivilege.canViewFeatureFlags
      )

    if (!canViewThisPage) return null

    return (
      <>
        <div className="flex min-h-full flex-col">
          <h2 className="text-headline-large">
            {tt.profile.view_feature_flags()}
          </h2>
          <div className="mb-3 text-body-large text-core-on-tertiary">
            {tt.profile.view_feature_flags_description()}
          </div>
          <div className="mt-6 flex-1 rounded-3xl bg-surface p-6">
            {!repository.featureFlags.isLoaded && (
              <div className="flex h-full flex-col items-center justify-center">
                <InfoIcon className="fill-fixed-accent-color" size={32} />
                <div className="mt-1 text-headline-small text-core-on-tertiary">
                  {tt.profile.loading_feature_flags()}
                </div>
              </div>
            )}

            {repository.featureFlags.isLoaded && (
              <ConfigViewer
                entries={
                  Object.entries(featureFlags).sort((a, b) =>
                    a[0].localeCompare(b[0])
                  ) as ConfigEntry[]
                }
              />
            )}
          </div>
          <ProfileViewConfig
            config={cubit.moderationConfig}
            title={tt.profile.moderation_config()}
          />
          <ProfileViewConfig
            config={cubit.gradingConfig}
            title={tt.profile.grading_config()}
          />
          <ProfileViewConfig
            config={cubit.transcriptionConfig}
            title={tt.profile.transcription_config()}
          />
        </div>
      </>
    )
  }
)

function ConfigViewer({ entries }: { entries: ConfigEntry[] }) {
  return (
    <div className="flex flex-col gap-y-4">
      {entries.map(([key, value]) => (
        <div
          key={key}
          className="flex items-center justify-between rounded-2xl bg-core-tertiary p-4"
        >
          <div className="text-body-medium">{key}</div>
          <div className="text-body-medium text-on-surface-var">
            {typeof value === 'boolean' ? (
              value ? (
                'true'
              ) : (
                'false'
              )
            ) : typeof value === 'number' ? (
              value.toString()
            ) : typeof value === 'string' ? (
              value
            ) : (
              <code className="whitespace-pre-wrap">
                {JSON.stringify(value, null, 2)}
              </code>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
